// dependencies
import React, { useRef } from "react";
import { z } from "zod";

// components
import {
	FormCheckboxGroup,
	FormDatePicker,
	FormField,
	FormMultipleSelect,
	FormSingleSelect,
	PanelFilters,
} from "@performyard/py-components";

import { MultipleSelectEmployee } from "../../../../../../components/Inputs/SelectEmployee";

// hooks
import { SearchServiceSort } from "../../../../../../api/hooks";
import { useOutlooks } from "../../../../../../api/hooks/defs/outlook";

// services
import { IStrategyCategory } from "../../../../../../api/services";

// utils
import { createFetchCategoryOptions, createFetchOutlookOptions } from "./filterDataFetchers";

const statusOptions = [
	{ label: "Active", value: "active" },
	{ label: "Completed", value: "done" },
	{ label: "Archived", value: "archived" },
];

const sortOptions = [
	{ name: "Name (Alphabetical)", id: "name", direction: "asc" },
	{ name: "Assignee (Alphabetical)", id: "assignee.name", direction: "asc" },
	{ name: "Supporting Goal(s)", id: "descendantsCount", direction: "asc" },
	{ name: "Progress", id: "progress", direction: "asc" },
];

const AlignmentFiltersSchema = z.object({
	currentSort: z
		.object({
			name: z.string(),
			direction: z.string().optional().default("asc"),
		})
		.optional(),
	assignee: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
	dueDateAfter: z.date().nullable().optional(),
	dueDateBefore: z.date().nullable().optional(),
	category: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
	status: z.array(z.string()).default([]),
	outlook: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })).default([]),
});

export type AlignmentFilters = z.infer<typeof AlignmentFiltersSchema>;

interface AlignmentFiltersProps {
	isOpen: boolean;
	onClose: () => void;
	filters: Partial<AlignmentFilters>;
	onApplyFilters: (filters: Partial<AlignmentFilters>, sort: SearchServiceSort) => void;
	disableSortBy?: boolean;
}

const AlignmentListFilters = ({
	isOpen,
	onClose,
	filters,
	onApplyFilters,
	disableSortBy = false,
}: AlignmentFiltersProps) => {
	const { data: outlooksData } = useOutlooks();
	const categoriesCache = useRef<IStrategyCategory[]>([]);

	const handleSubmit = ({
		value,
	}: {
		value: Partial<AlignmentFilters>;
		resetForm: () => void;
		setError: (field: keyof AlignmentFilters, error: string) => void;
	}) => {
		const formattedState = { ...value };
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key];
			if (
				filterValue === null ||
				filterValue === undefined ||
				(Array.isArray(filterValue) && filterValue.length === 0)
			) {
				delete formattedState[key];
			}
		});

		// Ensure currentSort has direction when name is present
		if (formattedState.currentSort?.name && !formattedState.currentSort?.direction) {
			formattedState.currentSort.direction = "asc";
		}

		onApplyFilters(formattedState, formattedState?.currentSort as SearchServiceSort);
		onClose();
	};

	const defaultValues: AlignmentFilters = {
		assignee: filters.assignee || [],
		dueDateAfter: filters.dueDateAfter,
		dueDateBefore: filters.dueDateBefore,
		category: filters.category || [],
		status: filters.status || [],
		outlook: filters.outlook || [],
		currentSort: filters.currentSort || { name: "name", direction: "asc" },
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={AlignmentFiltersSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				{!disableSortBy && (
					<FormSingleSelect<string>
						id="currentSort.name"
						label="Sort by"
						options={sortOptions.map((opt) => ({ label: opt.name, value: opt.id }))}
						selectionVariant="select"
					/>
				)}

				<FormField<{ id: string; name: string }[]>
					id="assignee"
					label="Assignee"
					input={(field) => (
						<MultipleSelectEmployee
							value={field.value || []}
							onChange={field.onChange}
							placeholder="Make a selection"
						/>
					)}
				/>

				<FormDatePicker id="dueDateAfter" label="Due Date After" clearable />

				<FormDatePicker id="dueDateBefore" label="Due Date Before" clearable />

				<FormMultipleSelect<{ id: string; name: string }>
					id="category"
					label="Category"
					fetchOptions={createFetchCategoryOptions(categoriesCache)}
					selectionVariant="combobox"
					placeholder="Make a selection"
				/>

				<FormCheckboxGroup id="status" label="Status" options={statusOptions} />

				<FormMultipleSelect<{ id: string; name: string; color: string }>
					id="outlook"
					label="Outlook"
					fetchOptions={createFetchOutlookOptions(outlooksData)}
					selectionVariant="combobox"
					placeholder="Make a selection"
					renderOption={(option) => (
						<div className="flex items-center gap-2">
							<div className="h-3 w-3 rounded-full" style={{ backgroundColor: option.value.color }} />
							<span>{option.label}</span>
						</div>
					)}
				/>
			</div>
		</PanelFilters>
	);
};

export default AlignmentListFilters;
